import React from "react";
import * as moment from "moment-timezone";
import "moment/locale/es";
import { PayrollVisitHourRow } from "../../utils/payrollVisitHours.ts";
import {
  formatPayrollHoursDecimal,
  formatPayrollHoursWithH,
} from "../../services/payroll/ui.ts";
type PayrollDayVisitRowsBaseProps = {
  visits: PayrollVisitHourRow[];
  dateKey: string;
  variant: "payment" | "chart";
};

type PayrollDayVisitRowsProps = PayrollDayVisitRowsBaseProps & {
  /** Por defecto solo filas de visita; el total va aparte al final. */
  includeTotal?: boolean;
};

const totalRowStyle: React.CSSProperties = {
  backgroundColor: "rgb(242 247 180)",
  fontWeight: "bold",
  color: "#000",
};

const visitRowStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
};

const visitCellNoBorder: React.CSSProperties = {
  borderBottom: "none",
  borderTop: "none",
};

const hoursCellStyle: React.CSSProperties = {
  padding: "4px 8px",
  textAlign: "center",
  fontSize: "0.72rem",
  fontVariantNumeric: "tabular-nums",
  verticalAlign: "middle",
};

const timeCellStyle: React.CSSProperties = {
  padding: "4px 8px",
  textAlign: "center",
  fontSize: "0.72rem",
  fontVariantNumeric: "tabular-nums",
  verticalAlign: "middle",
  backgroundColor: "#fffbeb",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
};

const visitBranchLabel = (visit: PayrollVisitHourRow): string => {
  const name = visit.branchName?.trim();
  if (name) {
    return name;
  }
  if (visit.branchId != null && Number(visit.branchId) > 0) {
    return `Sucursal #${visit.branchId}`;
  }
  return "";
};

const buildDateLabel = (dateKey: string): string =>
  moment.utc(dateKey, "YYYY-MM-DD").locale("es").format("[del] D MMM YYYY");

const sumVisitHours = (visits: PayrollVisitHourRow[]): number =>
  visits.reduce((acc, visit) => acc + (Number(visit.horasRegistradas) || 0), 0);

export const PayrollDayVisitDetailRows = ({
  visits,
  dateKey,
  variant,
}: PayrollDayVisitRowsBaseProps) => {
  if (visits.length <= 1) {
    return null;
  }

  if (variant === "payment") {
    return (
      <>
        {visits.map((visit, visitIndex) => (
          <tr
            key={`payment-visit-${dateKey}-${visitIndex}`}
            className="payroll-day-visit-row"
            style={visitRowStyle}
          >
            <td
              style={{
                ...visitCellNoBorder,
                padding: "4px 8px",
                textAlign: "center",
                fontSize: "0.65rem",
                color: "#94a3b8",
              }}
            />
            <td
              style={{
                ...visitCellNoBorder,
                ...hoursCellStyle,
                fontSize: "0.68rem",
                color: "#64748b",
              }}
            >
              {formatPayrollHoursWithH(visit.horasRegistradas)}
            </td>
            <td
              style={{
                ...visitCellNoBorder,
                padding: "4px 8px",
                textAlign: "center",
                fontSize: "0.68rem",
                color: "#94a3b8",
              }}
            >
              {" "}
            </td>
            <td
              style={{
                ...visitCellNoBorder,
                padding: "4px 8px",
                textAlign: "center",
                fontSize: "0.68rem",
                color: "#94a3b8",
              }}
            >
              {" "}
            </td>
            <td
              style={{
                ...visitCellNoBorder,
                padding: "4px 10px",
                fontSize: "0.68rem",
                color: "#475569",
                fontWeight: 500,
              }}
            >
              {visitBranchLabel(visit)}
            </td>
            <td style={visitCellNoBorder} />
            <td
              style={{
                ...visitCellNoBorder,
                ...timeCellStyle,
                fontSize: "0.68rem",
                color: "#334155",
              }}
            >
              {visit.entradaRegistrada}
            </td>
            <td
              style={{
                ...visitCellNoBorder,
                ...timeCellStyle,
                fontSize: "0.68rem",
                color: "#334155",
              }}
            >
              {visit.salidaRegistrada}
            </td>
            <td style={visitCellNoBorder} />
          </tr>
        ))}
      </>
    );
  }

  return (
    <>
      {visits.map((visit, visitIndex) => (
        <tr
          key={`chart-visit-${dateKey}-${visitIndex}`}
          className="payroll-day-visit-row"
          style={visitRowStyle}
        >
          <td
            style={{
              ...visitCellNoBorder,
              padding: "4px 8px",
              fontSize: "0.72rem",
              color: "#475569",
              fontWeight: 500,
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            {visitBranchLabel(visit)}
          </td>
          <td
            colSpan={2}
            style={{
              ...visitCellNoBorder,
              padding: "4px 8px",
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            {" "}
          </td>
          <td style={{ ...timeCellStyle, ...visitCellNoBorder }}>
            {visit.entradaRegistrada}
          </td>
          <td style={{ ...timeCellStyle, ...visitCellNoBorder }}>
            {visit.salidaRegistrada}
          </td>
          <td
            style={{
              ...visitCellNoBorder,
              ...hoursCellStyle,
              color: "#94a3b8",
            }}
          >
            {" "}
          </td>
          <td style={{ ...visitCellNoBorder, ...hoursCellStyle }}>
            {formatPayrollHoursDecimal(visit.horasRegistradas)}
          </td>
          <td
            style={{
              ...visitCellNoBorder,
              ...hoursCellStyle,
              color: "#94a3b8",
            }}
          >
            {" "}
          </td>
        </tr>
      ))}
    </>
  );
};

export const PayrollDayVisitTotalRow = ({
  visits,
  dateKey,
  variant,
}: PayrollDayVisitRowsBaseProps) => {
  if (visits.length <= 1) {
    return null;
  }

  const totalHours = sumVisitHours(visits);
  const dateLabel = buildDateLabel(dateKey);

  if (variant === "payment") {
    return (
      <tr className="payroll-day-visit-total" style={totalRowStyle}>
        <td
          style={{
            ...visitCellNoBorder,
            padding: "6px 10px",
            textAlign: "right",
            fontSize: "0.68rem",
          }}
        >
          <strong>Horas totales {dateLabel}</strong>
        </td>
        <td
          style={{
            ...visitCellNoBorder,
            ...hoursCellStyle,
            padding: "6px 10px",
            fontSize: "0.68rem",
            fontWeight: "bold",
          }}
        >
          {formatPayrollHoursDecimal(totalHours)}
        </td>
        <td colSpan={7} style={visitCellNoBorder} />
      </tr>
    );
  }

  return (
    <tr className="payroll-day-visit-total" style={totalRowStyle}>
      <td
        colSpan={6}
        style={{
          ...visitCellNoBorder,
          padding: "6px 10px",
          textAlign: "right",
          verticalAlign: "middle",
        }}
      >
        <strong>Horas totales {dateLabel}</strong>
      </td>
      <td
        style={{
          ...visitCellNoBorder,
          ...hoursCellStyle,
          padding: "6px 10px",
          fontWeight: "bold",
        }}
      >
        {formatPayrollHoursDecimal(totalHours)}
      </td>
      <td
        style={{
          ...visitCellNoBorder,
          padding: "6px 10px",
          textAlign: "center",
        }}
      >
        {" "}
      </td>
    </tr>
  );
};

const PayrollDayVisitRows = ({
  visits,
  dateKey,
  variant,
  includeTotal = true,
}: PayrollDayVisitRowsProps) => {
  if (visits.length <= 1) {
    return null;
  }

  return (
    <>
      <PayrollDayVisitDetailRows
        visits={visits}
        dateKey={dateKey}
        variant={variant}
      />
      {includeTotal ? (
        <PayrollDayVisitTotalRow
          visits={visits}
          dateKey={dateKey}
          variant={variant}
        />
      ) : null}
    </>
  );
};

export default PayrollDayVisitRows;
